// Search API service
import { baseClient } from './baseClient';

export interface SearchResult {
  id: string;
  name: string;
  type: 'Account' | 'Contact' | 'Deal' | 'Task';
  description?: string;
  metadata?: Record<string, any>;
}

export interface SearchOptions {
  q: string;
  types?: string[];
  limit?: number;
  offset?: number;
  filters?: Record<string, any>;
}

export class SearchApi {
  private static instance: SearchApi;

  private constructor() { }

  public static getInstance(): SearchApi {
    if (!SearchApi.instance) {
      SearchApi.instance = new SearchApi();
    }
    return SearchApi.instance;
  }

  /**
   * Global search across all entities
   */
  async globalSearch(options: SearchOptions): Promise<SearchResult[]> {
    const params = new URLSearchParams();
    params.append('q', options.q);

    if (options.types) {
      options.types.forEach(type => params.append('types', type));
    }
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    return baseClient.get<SearchResult[]>(`/search?${params.toString()}`);
  }

  /**
   * Search deals
   */
  async searchDeals(query: string, filters?: Record<string, any>): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('q', query);
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        params.append(key, value.toString());
      });
    }

    return baseClient.get<any[]>(`/search/deals/?${params.toString()}`);
  }

  /**
   * Search contacts
   */
  async searchContacts(query: string, filters?: Record<string, any>): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('q', query);
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        params.append(key, value.toString());
      });
    }

    return baseClient.get<any[]>(`/search/contacts/?${params.toString()}`);
  }

  /**
   * Search accounts
   */
  async searchAccounts(query: string, filters?: Record<string, any>): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('q', query);
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        params.append(key, value.toString());
      });
    }

    return baseClient.get<any[]>(`/search/accounts/?${params.toString()}`);
  }

  /**
   * Search tasks
   */
  async searchTasks(query: string, filters?: Record<string, any>): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('q', query);
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        params.append(key, value.toString());
      });
    }

    return baseClient.get<any[]>(`/search/tasks/?${params.toString()}`);
  }

  /**
   * Search activities
   */
  async searchActivities(query: string, filters?: Record<string, any>): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('q', query);
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        params.append(key, value.toString());
      });
    }

    return baseClient.get<any[]>(`/search/activities/?${params.toString()}`);
  }

  /**
   * Get search suggestions
   */
  async getSearchSuggestions(query: string, limit: number = 5): Promise<string[]> {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('limit', limit.toString());

    return baseClient.get<string[]>(`/search/suggestions/?${params.toString()}`);
  }

  /**
   * Get search history
   */
  async getSearchHistory(): Promise<string[]> {
    return baseClient.get<string[]>('/search/history');
  }

  /**
   * Save search query to history
   */
  async saveSearchQuery(query: string): Promise<void> {
    return baseClient.post<void>('/search/history', { query });
  }

  /**
   * Clear search history
   */
  async clearSearchHistory(): Promise<void> {
    return baseClient.delete<void>('/search/history');
  }

  /**
   * Get popular searches
   */
  async getPopularSearches(limit: number = 10): Promise<string[]> {
    return baseClient.get<string[]>(`/search/popular?limit=${limit}`);
  }

  /**
   * Get search statistics
   */
  async getSearchStats(): Promise<any> {
    return baseClient.get<any>('/search/stats');
  }

  /**
   * Advanced search with multiple criteria
   */
  async advancedSearch(criteria: {
    query?: string;
    types?: string[];
    dateRange?: { start: string; end: string };
    filters?: Record<string, any>;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }): Promise<SearchResult[]> {
    const params = new URLSearchParams();

    if (criteria.query) params.append('q', criteria.query);
    if (criteria.types) {
      criteria.types.forEach(type => params.append('types', type));
    }
    if (criteria.dateRange) {
      params.append('start_date', criteria.dateRange.start);
      params.append('end_date', criteria.dateRange.end);
    }
    if (criteria.filters) {
      Object.entries(criteria.filters).forEach(([key, value]) => {
        params.append(`filters[${key}]`, value.toString());
      });
    }
    if (criteria.sortBy) params.append('sort_by', criteria.sortBy);
    if (criteria.sortOrder) params.append('sort_order', criteria.sortOrder);
    if (criteria.limit) params.append('limit', criteria.limit.toString());
    if (criteria.offset) params.append('offset', criteria.offset.toString());

    return baseClient.get<SearchResult[]>(`/search/advanced/?${params.toString()}`);
  }

  /**
   * Search within specific entity relationships
   */
  async searchRelated(entityType: string, entityId: string, query: string): Promise<SearchResult[]> {
    const params = new URLSearchParams();
    params.append('q', query);

    return baseClient.get<SearchResult[]>(`/search/${entityType}/${entityId}/related/?${params.toString()}`);
  }

  /**
   * Get search facets for filtering
   */
  async getSearchFacets(query: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('q', query);

    return baseClient.get<any>(`/search/facets/?${params.toString()}`);
  }
}

// Export singleton instance
export const searchApi = SearchApi.getInstance(); 